import React from "react";
import { Box, Typography, Chip } from "@mui/material";
import { AccessTime, Schedule } from "@mui/icons-material";
import { BaseActionRendererProps, getActionData } from "./BaseActionRenderer";

interface CalendarMoveData {
  eventId?: string;
  newStartTime?: string;
  newEndTime?: string;
  notifyAttendees?: boolean;
  title?: string;
  description?: string;
  location?: string;
  timezone?: string;
}

export const CalendarMoveActionRenderer: React.FC<BaseActionRendererProps> = ({ actionData }) => {
  const moveData = getActionData(actionData) as CalendarMoveData;

  const title = moveData.title || actionData.title || "Move Calendar Event";
  const description = moveData.description;
  const location = moveData.location;
  const newStartTime = moveData.newStartTime;
  const newEndTime = moveData.newEndTime;
  const timezone = moveData.timezone;
  const notifyAttendees = moveData.notifyAttendees;

  // Format dates for display
  const formatDateTime = (dateTime: string, tz?: string) => {
    if (!dateTime) return "";

    const date = new Date(dateTime);
    if (isNaN(date.getTime())) return dateTime;

    // Use event timezone if available, otherwise use default
    const displayTz = tz || "UTC";

    try {
      return date.toLocaleString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: displayTz,
      });
    } catch (error) {
      return dateTime;
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body1" sx={{ fontWeight: 500 }} gutterBottom>
        Move Calendar Event: {title}
      </Typography>

      {description && (
        <Typography variant="body2" gutterBottom sx={{ mb: 1, fontStyle: "italic" }}>
          {description}
        </Typography>
      )}

      {/* New time information */}
      {(newStartTime || newEndTime) && (
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Schedule sx={{ fontSize: 16, color: "primary.main" }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              New Time:
            </Typography>
          </Box>
          <Box sx={{ ml: 3 }}>
            {newStartTime && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <b>Start:</b> {formatDateTime(newStartTime, timezone)}
              </Typography>
            )}
            {newEndTime && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <b>End:</b> {formatDateTime(newEndTime, timezone)}
              </Typography>
            )}
            {timezone && (
              <Typography variant="caption" color="text.secondary">
                Timezone: {timezone}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Location information */}
      {location && (
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <AccessTime sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Location:
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ ml: 3 }}>
            {location}
          </Typography>
        </Box>
      )}

      {/* Notification settings */}
      {notifyAttendees !== undefined && (
        <Box sx={{ mb: 1 }}>
          <Chip label={notifyAttendees ? "Will notify attendees" : "Will not notify attendees"} size="small" color={notifyAttendees ? "primary" : "default"} variant="outlined" />
        </Box>
      )}

      {actionData.reason && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", mt: 1 }}>
          Reason: {actionData.reason}
        </Typography>
      )}
    </Box>
  );
};
